import { useCallback } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';

export interface GetOrCreateResult {
  ok: boolean;
  conversationId?: string;
  error?: string;
}

export const useOrCreateConversation = () => {
  const { user } = useAuth();

  const getOrCreate = useCallback(
    async (otherUserId: string): Promise<GetOrCreateResult> => {
      if (!user) return { ok: false, error: 'Not logged in.' };
      if (otherUserId === user.id) return { ok: false, error: 'Cannot message yourself.' };

      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'No connection.' };

      const [a, b] = [user.id, otherUserId].sort() as [string, string];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;

      const { data: existing } = await db
        .from('hottr_conversations')
        .select('id')
        .eq('participant_a', a)
        .eq('participant_b', b)
        .maybeSingle() as { data: { id: string } | null };

      if (existing) return { ok: true, conversationId: existing.id };

      const { data: created, error } = await db
        .from('hottr_conversations')
        .insert({ participant_a: a, participant_b: b })
        .select('id')
        .single() as { data: { id: string } | null; error: { message: string } | null };

      if (error) return { ok: false, error: error.message };
      return { ok: true, conversationId: created!.id };
    },
    [user?.id],
  );

  return { getOrCreate };
};
